import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, of, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
  StepperDataPayload,
  StepperDataResponse,
  StepperDataService,
} from './stepper-data.service';

interface StepperDataState {
  draftExists: boolean;
  draft: StepperDataResponse | null;
}

const initialState: StepperDataState = {
  draftExists: false,
  draft: null,
};

export const StepperDataStore = signalStore(
  withState(initialState),
  withMethods((store, service = inject(StepperDataService)) => ({
    loadDraft: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { draftExists: false, draft: null })),
        switchMap(stepperName =>
          service.getStepperData(stepperName).pipe(
            tap(({ res }) =>
              patchState(store, { draftExists: !!res, draft: res ?? null })
            ),
            catchError(() => {
              patchState(store, { draftExists: false, draft: null });
              return of(null);
            })
          )
        )
      )
    ),

    saveDraft: rxMethod<StepperDataPayload>(
      pipe(
        switchMap(payload =>
          service.createStepperData(payload).pipe(
            tap(({ res }) =>
              patchState(store, { draftExists: !!res, draft: res ?? null })
            ),
            catchError(() => of(null))
          )
        )
      )
    ),

    deleteDraft: rxMethod<void>(
      pipe(
        switchMap(() => {
          const id = store.draft()?._id;
          if (!id) {
            patchState(store, { draftExists: false, draft: null });
            return EMPTY;
          }
          return service.deleteStepperData(id).pipe(
            tap(() => patchState(store, { draftExists: false, draft: null })),
            catchError(() => of(null))
          );
        })
      )
    ),
  }))
);
